// Bilingual translations: English & Arabic
export type Language = "en" | "ar";

export const translations = {
  en: {
    // Navigation
    nav: {
      home: "Home",
      portfolio: "Portfolio",
      about: "About",
      services: "Services",
      contact: "Contact",
      switchLang: "العربية",
    },
    // Home Page
    home: {
      heroTagline: "Brand Identity Designer",
      heroTitle: "Crafting Logos That",
      heroTitleHighlight: "Tell Stories",
      heroSubtitle:
        "I transform ideas into iconic visual identities. Every mark I create carries purpose, precision, and timeless elegance.",
      ctaWork: "View My Work",
      ctaContact: "Start a Project",
      statsProjects: "Projects Completed",
      statsClients: "Happy Clients",
      statsYears: "Years Experience",
      statsAwards: "Design Awards",
      featuredWork: "Featured Work",
      featuredSubtitle: "A selection of recent brand identity projects",
      viewAll: "View All Projects",
      processTitle: "My Creative Process",
      processSubtitle: "From concept to iconic mark in four stages",
      process1Title: "Discover",
      process1Desc: "Deep dive into your brand story, values, and target audience to build a strategic foundation.",
      process2Title: "Conceptualize",
      process2Desc: "Exploring visual directions through sketching, moodboards, and strategic ideation.",
      process3Title: "Design",
      process3Desc: "Crafting refined digital marks with precision, balance, and visual harmony.",
      process4Title: "Deliver",
      process4Desc: "Full brand package with files for every medium — print, digital, and beyond.",
      testimonialsTitle: "What Clients Say",
    },
    // Portfolio Page
    portfolio: {
      title: "My Portfolio",
      subtitle: "A showcase of brand identities crafted with purpose and precision",
      filterAll: "All",
      filterBrand: "Brand Identity",
      filterCorporate: "Corporate",
      filterSocial: "Social Media",
      viewCase: "View Case Study",
      category: "Category",
      year: "Year",
      client: "Client",
    },
    // About Page
    about: {
      title: "About Me",
      subtitle: "Passionate about the art of visual identity",
      bio1:
        "I'm a brand identity designer with over 8 years of experience creating logos and visual systems for companies worldwide. My work lives at the intersection of strategy and aesthetics.",
      bio2:
        "Every logo I design starts with a question: what story does this brand need to tell? The answer shapes every curve, weight, and color choice.",
      skillsTitle: "Core Skills",
      experienceTitle: "Experience",
      educationTitle: "Education",
      awardsTitle: "Awards & Recognition",
      downloadCV: "Download CV",
    },
    // Services Page
    services: {
      title: "Services",
      subtitle: "Tailored branding solutions for every stage of your business",
      popular: "Most Popular",
      getStarted: "Get Started",
      contactUs: "Contact for Quote",
      basicTitle: "الأساسيات",
      basicDesc: "A strong and professional start for your project. Perfect for simple, effective representation.",
      proTitle: "الاحترافي",
      proDesc: "Visual identity that sets you apart from competitors. Your ideal choice for growth.",
      premiumTitle: "المتميز",
      premiumDesc: "Complete visual identity to build a real brand. A powerful and integrated presence.",
      additionalServices: "Additional Services",
      socialPost: "Social Media Post",
      namingBrand: "Brand Naming",
      fullSocialPackage: "Full Social Package",
      faqTitle: "Frequently Asked Questions",
    },
    // Contact Page
    contact: {
      title: "Start a Project",
      subtitle: "Let's create something remarkable together",
      nameLabel: "Full Name",
      namePlaceholder: "Your name",
      emailLabel: "Email Address",
      emailPlaceholder: "your@email.com",
      projectLabel: "Project Type",
      projectPlaceholder: "Select project type",
      budgetLabel: "Budget Range",
      budgetPlaceholder: "Select budget",
      messageLabel: "Project Brief",
      messagePlaceholder: "Tell me about your brand and what you're looking for...",
      sendBtn: "Send Message",
      sending: "Sending...",
      successTitle: "Message Sent!",
      successMsg: "Thank you! I'll get back to you within 24 hours.",
      orContact: "Or reach me directly",
    },
    // Footer
    footer: {
      rights: "All rights reserved.",
      madeWith: "Crafted with passion",
    },
  },
  ar: {
    // Navigation
    nav: {
      home: "الرئيسية",
      portfolio: "أعمالي",
      about: "عني",
      services: "الخدمات",
      contact: "تواصل",
      switchLang: "English",
    },
    // Home Page
    home: {
      heroTagline: "مصمم الهوية البصرية",
      heroTitle: "شعارات تحكي",
      heroTitleHighlight: "قصتك",
      heroSubtitle:
        "أحوّل الأفكار إلى هويات بصرية خالدة. كل علامة أصممها تحمل هدفاً ودقةً وأناقةً لا تفنى.",
      ctaWork: "استعرض أعمالي",
      ctaContact: "ابدأ مشروعك",
      statsProjects: "مشروع مكتمل",
      statsClients: "عميل سعيد",
      statsYears: "سنوات خبرة",
      statsAwards: "جائزة تصميم",
      featuredWork: "أبرز الأعمال",
      featuredSubtitle: "مختارات من مشاريع الهوية البصرية الأخيرة",
      viewAll: "عرض جميع المشاريع",
      processTitle: "عملية الإبداع",
      processSubtitle: "من الفكرة إلى العلامة الأيقونية في أربع مراحل",
      process1Title: "الاستكشاف",
      process1Desc: "غوص عميق في قصة علامتك التجارية وقيمها وجمهورك المستهدف لبناء أساس استراتيجي.",
      process2Title: "التصور",
      process2Desc: "استكشاف التوجهات البصرية من خلال الرسم والألواح المزاجية والتفكير الاستراتيجي.",
      process3Title: "التصميم",
      process3Desc: "صياغة علامات رقمية محكمة بدقة وتوازن وانسجام بصري.",
      process4Title: "التسليم",
      process4Desc: "حزمة علامة تجارية كاملة بملفات لكل وسيلة — طباعة ورقمية وما هو أبعد.",
      testimonialsTitle: "ماذا يقول العملاء",
    },
    // Portfolio Page
    portfolio: {
      title: "معرض أعمالي",
      subtitle: "عرض للهويات البصرية المصممة بهدف ودقة",
      filterAll: "الكل",
      filterBrand: "هوية بصرية",
      filterCorporate: "شركات",
      filterSocial: "سوشيال ميديا",
      viewCase: "عرض دراسة الحالة",
      category: "الفئة",
      year: "السنة",
      client: "العميل",
    },
    // About Page
    about: {
      title: "عني",
      subtitle: "شغف بفن الهوية البصرية",
      bio1:
        "أنا مصمم هوية بصرية بخبرة تزيد على 8 سنوات في إنشاء الشعارات والأنظمة البصرية للشركات حول العالم. يقع عملي عند تقاطع الاستراتيجية والجماليات.",
      bio2:
        "يبدأ كل شعار أصممه بسؤال: ما القصة التي تحتاج هذه العلامة التجارية لرويها؟ تشكّل الإجابة كل منحنى ووزن واختيار لوني.",
      skillsTitle: "المهارات الأساسية",
      experienceTitle: "الخبرة",
      educationTitle: "التعليم",
      awardsTitle: "الجوائز والتكريم",
      downloadCV: "تحميل السيرة الذاتية",
    },
    // Services Page
    services: {
      title: "الخدمات",
      subtitle: "حلول علامة تجارية مخصصة لكل مرحلة من مراحل عملك",
      popular: "الأكثر طلباً",
      getStarted: "ابدأ الآن",
      contactUs: "تواصل للاستفسار",
      basicTitle: "الأساسيات",
      basicDesc: "بداية قوية واحترافية لمشروعك. لو لسه بتبدأ وعايز شكل احترافي بسيط يعبّر عنك",
      proTitle: "الاحترافي",
      proDesc: "هوية بصرية تميزك وسط المنافسين. اختيارك المثالي",
      premiumTitle: "المتميز",
      premiumDesc: "هوية بصرية كاملة تبني براند حقيقي. لأصحاب المشاريع بشكل قوي و متكامل",
      additionalServices: "خدمات إضافية",
      socialPost: "بوست سوشيال ميديا",
      namingBrand: "تسمية البراند",
      fullSocialPackage: "باكدچ سوشيال كاملة",
      faqTitle: "الأسئلة الشائعة",
    },
    // Contact Page
    contact: {
      title: "ابدأ مشروعاً",
      subtitle: "لنبتكر معاً شيئاً استثنائياً",
      nameLabel: "الاسم الكامل",
      namePlaceholder: "اسمك",
      emailLabel: "البريد الإلكتروني",
      emailPlaceholder: "بريدك@example.com",
      projectLabel: "نوع المشروع",
      projectPlaceholder: "اختر نوع المشروع",
      budgetLabel: "نطاق الميزانية",
      budgetPlaceholder: "اختر الميزانية",
      messageLabel: "نبذة عن المشروع",
      messagePlaceholder: "أخبرني عن علامتك التجارية وما تبحث عنه...",
      sendBtn: "إرسال الرسالة",
      sending: "جارٍ الإرسال...",
      successTitle: "تم الإرسال!",
      successMsg: "شكراً لك! سأرد عليك خلال 24 ساعة.",
      orContact: "أو تواصل معي مباشرة",
    },
    // Footer
    footer: {
      rights: "جميع الحقوق محفوظة.",
      madeWith: "صُنع بشغف",
    },
  },
};

export type TranslationKey = keyof typeof translations.en;
